import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from '../ui/Modal';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Button } from '../ui/Button';
import { FilterableSelect } from '../ui/FilterableSelect';
import { DELIVERY_STATUSES, STATUS_MAP } from '../../utils/catalogs';
import { massiveChangeStatus } from '../../features/deliveries/deliveriesThunks';

const statusOptions = DELIVERY_STATUSES.map((status) => ({
  value: status.system_name,
  label: status.user_name,
}));

const CONSEQUENCES = [
  'Al cliente le llega un correo cuando el nuevo estado lo avisa.',
  'Se notifica a tus webhooks y, si corresponde, a la tienda y al TMS.',
  'Los finalizados y los que no son de tu operación no cambian.',
  'No se puede deshacer.',
];

/**
 * Changes the status of several shipments at once, from the deliveries history
 * and from Alerta.
 *
 * Takes **order ids**, not delivery ids: `deliveries/change_status/` filters
 * with `order__id__in`. The tables that open this modal select by
 * `delivery_id`, so each of them derives the order ids and passes those.
 *
 * How many changed is said by the toast of `massiveChangeStatus`; a failure is
 * said here, and the dialog stays open to try again.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.selectedOrderIds
 * @param {() => void} [props.onSuccess]
 */
export const MassiveChangeStatusModal = ({ isOpen, onClose, selectedOrderIds = [], onSuccess }) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState('');
  const [substatus, setSubstatus] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Every status carries its own substatuses — `failed` has thirteen,
  // `delivered` two. The backend does not check that they match, so the
  // dialog only offers the ones that hang from the status being set.
  const substatusOptions = useMemo(
    () =>
      (STATUS_MAP[status]?.substatus || []).map((option) => ({
        value: option.system_name,
        label: option.user_name,
      })),
    [status]
  );

  const reset = () => {
    setStatus('');
    setSubstatus('');
    setConfirming(false);
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    // The previous substatus almost never hangs from the new status.
    setSubstatus('');
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);

    const result = await dispatch(massiveChangeStatus({ ids: selectedOrderIds, status, substatus }));
    setSaving(false);
    setConfirming(false);

    // No answer: it may have been applied, which the page's warning says. The
    // table is refreshed to show what happened, and nothing invites a retry.
    if (massiveChangeStatus.fulfilled.match(result) || result.meta?.unconfirmed) {
      reset();
      onSuccess?.();
      onClose();
      return;
    }
    setError(typeof result.payload === 'string' ? result.payload : 'No se pudo cambiar el estado');
  };

  const count = selectedOrderIds.length;
  const noun = count === 1 ? 'envío' : 'envíos';
  const statusLabel = STATUS_MAP[status]?.user_name || status;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Modificar Estado"
        testId="massive-change-status-modal"
        busy={saving}
        error={error}
        errorTitle="No se pudo aplicar"
      >
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Se aplicará a {count} {noun}.
          </p>

          <FilterableSelect
            label="Nuevo estado"
            variant="form"
            value={status}
            onChange={handleStatusChange}
            options={statusOptions}
            placeholder="Selecciona un estado"
          />

          <FilterableSelect
            label="Sub-estado"
            variant="form"
            value={substatus}
            onChange={setSubstatus}
            options={substatusOptions}
            placeholder={status ? 'Selecciona un sub-estado' : 'Elige primero un estado'}
            disabled={!status}
          />

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={() => setConfirming(true)}
              disabled={!status || !substatus || count === 0}
            >
              Modificar {count > 0 ? `(${count})` : ''}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleConfirm}
        variant="primary"
        title="Cambiar el estado de estos envíos"
        message={`Vas a dejar ${count} ${noun} en «${statusLabel}».`}
        consequences={CONSEQUENCES}
        confirmLabel={`Cambiar ${count} ${noun}`}
        loadingLabel="Cambiando..."
        loading={saving}
        testId="massive-change-status-confirm"
      />
    </>
  );
};

export default MassiveChangeStatusModal;
